import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type MutableRefObject,
} from 'react'
import { Box, Group, rem, Stack, Text, useMantineTheme } from '@mantine/core'
import {
  BackButton,
  BOGGLE_CHALLENGE_BORDER_ACCENT_KEY,
  ChallengeButton,
  PrimaryButton,
  SecondaryButton,
} from '~/boggle/ui/atoms/Buttons'
import { resolveBoggleAccentCssColor } from '~/boggle/ui/atoms/boggleAccentColor'
import { RampingNumberText } from '~/boggle/ui/atoms/RampingNumberText'
import {
  BoggleBadgeList,
  BoggleBadgeRevealPad,
} from '~/boggle/ui/run/BoggleBadgeRevealStage'
import type { BoggleBadgeRevealPhase } from '~/boggle/ui/run/useRunResultsChoreography'
import tierMeterStyles from './RunSummaryTierMeter.module.css'
import { GradientText } from '../atoms/Text'
import {
  BoggleRunSummaryTierLevel,
  getTierForMetric,
  type BoggleRunSummaryScore,
  type BoggleRunSummaryTierLine,
} from '~/boggle/config/runSummaryScore'
import { IconGlyph } from '../atoms/Icons'
import { FastForwardIcon } from '@phosphor-icons/react'
import {
  formatRunSummaryMetricValue,
  RUN_SUMMARY_TIER_FILL_COLORS,
} from './runSummaryTierPresentation'

export type BoggleChallengeSummaryOutcome = 'ace' | 'success' | 'target-missed'
export type BoggleStandardSummaryOutcome = 'new-best' | 'tie'

export const RUN_SUMMARY_METRIC_RAMP_MS = 1400

const Label = Text.withProps({ fz: 14, lh: 1.1 })

/** In-flow reserve for the badge list + bottom reveal pad (absolute inside). */
const RUN_SUMMARY_BADGE_STAGE_RESERVE_PX = 140

export function RunSummary({
  accentColor = 'yellow.4',
  actions,
  badgeAnchorsRef,
  badgeRevealPhases,
  challengeOutcome,
  disablePrimarySummaryActions = false,
  onBack,
  onNewGame,
  revealedTierCount,
  showOutcomeCopy,
  summaryScore,
  showSkipButton = false,
  skipSummaryScoringAnimations,
  skippedSummaryScoringAnimations = false,
  standardSummaryOutcome = null,
  tierMetricRampDurationMs = RUN_SUMMARY_METRIC_RAMP_MS,
  tierValueRefs,
}: {
  /** Border color for Play Again (hex, rgb, or Mantine key); fill stays default `dark.7` gradient. */
  accentColor?: string
  actions?: {
    label: string
    onClick: () => void
    tone?: 'challenge' | 'cta' | 'neutral' | 'warm'
  }[]
  /** Capture refs for each bottom-reveal medallion's tag anchor — used as `FloatingScoreTag` anchors. */
  badgeAnchorsRef?: MutableRefObject<(HTMLDivElement | null)[]>
  /** Per-badge presentation phase from `useRunResultsChoreography`. */
  badgeRevealPhases: readonly BoggleBadgeRevealPhase[]
  challengeOutcome?: BoggleChallengeSummaryOutcome | null
  /** Locks Play Again / CTAs until run-results scoring choreography finishes. */
  disablePrimarySummaryActions?: boolean
  onBack: () => void
  onNewGame: () => void
  /** Number of tier lines currently revealed (driven by `useRunResultsChoreography`). */
  revealedTierCount: number
  showOutcomeCopy: boolean
  summaryScore: BoggleRunSummaryScore
  showSkipButton?: boolean
  skipSummaryScoringAnimations?: () => void
  skippedSummaryScoringAnimations?: boolean
  standardSummaryOutcome?: BoggleStandardSummaryOutcome | null
  tierMetricRampDurationMs?: number
  /** Capture refs for each tier line's value element — used as `FloatingScoreTag` anchors. */
  tierValueRefs?: MutableRefObject<(HTMLDivElement | null)[]>
}) {
  const theme = useMantineTheme()
  /** Shared positional anchor for the BadgeList ↔ RevealPad FLIP move. */
  const bottomAnchorRef = useRef<HTMLDivElement | null>(null)
  const resolvedActions = actions ?? [
    {
      label: 'Play Again',
      onClick: onNewGame,
      tone: 'warm' as const,
    },
  ]
  const challengeOutcomeCopy =
    challengeOutcome === 'ace'
      ? {
          label: 'Ace!',
          textColor: '#f0c85a',
          fontSize: 42,
        }
      : challengeOutcome === 'success'
        ? {
            label: 'Success!',
            textColor: '#79d98c',
            fontSize: 24,
          }
        : challengeOutcome === 'target-missed'
          ? {
              label: 'Not quite...',
              textColor: '#ffb09b',
              fontStyle: 'italic',
              fontSize: 20,
            }
          : null

  const standardPersonalBestCopy =
    standardSummaryOutcome === 'new-best'
      ? {
          fontSize: 28,
          label: 'New best score!',
          textColor: '#f0c85a',
        }
      : standardSummaryOutcome === 'tie'
        ? {
            fontSize: 20,
            fontStyle: 'italic' as const,
            label: 'So close...',
            textColor: 'white',
          }
        : null

  return (
    <Stack align="center" gap={20} h="100%" justify="space-between" mih={0}>
      <Stack
        align="stretch"
        flex={1}
        gap={20}
        bdrs={12}
        mih={0}
        pb={12}
        pt={16}
        px={10}
        style={{
          minHeight: 0,
          overflow: 'hidden',
        }}
        w="100%"
      >
        <Stack gap={2} px={4} mb={20}>
          {summaryScore.tierLines.map((line, index) => (
            <RunSummaryTierLine
              isRevealed={index < revealedTierCount}
              key={line.category.id}
              line={line}
              longestWord={summaryScore.stats.longestWord}
              metricRampDurationMs={tierMetricRampDurationMs}
              valueRef={el => {
                if (tierValueRefs) {
                  tierValueRefs.current[index] = el
                }
              }}
            />
          ))}
        </Stack>

        {summaryScore.badgeLines.length > 0 ? (
          <Box
            aria-label="Badge results"
            data-testid="run-summary-badge-stage"
            h={RUN_SUMMARY_BADGE_STAGE_RESERVE_PX}
            pos="relative"
            style={{ flexShrink: 0 }}
            w="100%"
          >
            <Box inset={0} pos="absolute" style={{ overflow: 'visible' }}>
              <BoggleBadgeList
                badgeLines={summaryScore.badgeLines}
                bottomAnchorRef={bottomAnchorRef}
                phases={badgeRevealPhases}
              />
              <Group
                justify="center"
                left={0}
                pos="absolute"
                right={0}
                style={{ pointerEvents: 'none', zIndex: 2 }}
                top={36}
              >
                <BoggleBadgeRevealPad
                  badgeAnchorsRef={badgeAnchorsRef}
                  badgeLines={summaryScore.badgeLines}
                  bottomAnchorRef={bottomAnchorRef}
                  phases={badgeRevealPhases}
                />
              </Group>
            </Box>
          </Box>
        ) : null}

        {challengeOutcomeCopy ? (
          <Group
            gap={10}
            justify="center"
            opacity={showOutcomeCopy ? 1 : 0}
            style={{
              pointerEvents: showOutcomeCopy ? undefined : 'none',
              transition: 'opacity 360ms ease-out',
            }}
          >
            {challengeOutcome === 'ace' ? (
              <GradientText
                color={challengeOutcomeCopy.textColor}
                fz={challengeOutcomeCopy.fontSize}
                ff="'Tilt Neon', sans-serif"
                fw={600}
                ta="center"
                fs={challengeOutcomeCopy.fontStyle}
              >
                {challengeOutcomeCopy.label}
              </GradientText>
            ) : (
              <Text
                c={challengeOutcomeCopy.textColor}
                fz={challengeOutcomeCopy.fontSize}
                ff="'Tilt Neon', sans-serif"
                fw={600}
                ta="center"
                fs={challengeOutcomeCopy.fontStyle}
              >
                {challengeOutcomeCopy.label}
              </Text>
            )}
          </Group>
        ) : null}
      </Stack>

      <Stack align="center" gap={12} style={{ flexShrink: 0 }} w="100%">
        <Stack gap={12} maw={320} w="100%" align="center">
          {skipSummaryScoringAnimations !== undefined &&
          !skippedSummaryScoringAnimations &&
          (showSkipButton ||
            (showOutcomeCopy && standardPersonalBestCopy !== null)) ? (
            <Box
              h={54}
              style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
              }}
              w="100%"
            >
              {showSkipButton ? (
                <SecondaryButton
                  fw={400}
                  fz={18}
                  h={54}
                  onClick={skipSummaryScoringAnimations}
                  px={16}
                  w={160}
                  rightSection={
                    <IconGlyph icon={FastForwardIcon} size={24} weight="fill" />
                  }
                >
                  Skip
                </SecondaryButton>
              ) : standardPersonalBestCopy ? (
                <Stack
                  align="center"
                  gap={2}
                  opacity={showOutcomeCopy ? 1 : 0}
                  style={{
                    pointerEvents: showOutcomeCopy ? undefined : 'none',
                    transition: 'opacity 360ms ease-out',
                  }}
                >
                  <GradientText
                    color={standardPersonalBestCopy.textColor}
                    fz={standardPersonalBestCopy.fontSize}
                    ff="'Tilt Neon', sans-serif"
                    fw={400}
                    fs={standardPersonalBestCopy.fontStyle}
                    ta="center"
                  >
                    {standardPersonalBestCopy.label}
                  </GradientText>
                </Stack>
              ) : null}
            </Box>
          ) : null}
          {resolvedActions.map(action =>
            action.tone === 'cta' ? (
              <ChallengeButton
                disabled={disablePrimarySummaryActions}
                styles={{ root: { transition: 'none' } }}
                w={220}
                key={action.label}
                onClick={action.onClick}
              >
                {action.label}
              </ChallengeButton>
            ) : (
              <PrimaryButton
                bd={`2px solid ${resolveBoggleAccentCssColor(
                  action.tone === 'challenge'
                    ? BOGGLE_CHALLENGE_BORDER_ACCENT_KEY
                    : accentColor,
                  theme,
                )}`}
                bdrs={action.tone === 'neutral' ? 24 : 50}
                c="white"
                color="dark.7"
                disabled={disablePrimarySummaryActions}
                styles={{ root: { transition: 'none' } }}
                w={220}
                h={64}
                fw={500}
                key={action.label}
                onClick={action.onClick}
              >
                {action.label}
              </PrimaryButton>
            ),
          )}

          <BackButton
            bdrs={50}
            c="white"
            h={44}
            iconSize={16}
            onClick={onBack}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}

const RUN_SUMMARY_TIER_METER_TICK_UNREACHED = '#666666'
const RUN_SUMMARY_TIER_METER_TICK_REACHED = '#ffffff'
/** Max tier filled bar accent (tier V → user “tier 5”). */
const RUN_SUMMARY_TIER_METER_MAX_GLOW = '0 0 6px rgba(245, 220, 150, 0.95)'

const TIER_LINE_METER_WIDTH = 125
/**
 * Pixels from the track’s right end to the final tier tick (visual scale extends past
 * the top tier threshold so ticks sit on a longer “ruler”; top-tier runs still fill 100%).
 */
const RUN_SUMMARY_TIER_METER_END_GAP_PX = 5

type RunSummaryTierMeterHandle = {
  paint: (metricValue: number) => void
  reset: () => void
}

type TierTickRefs = Partial<
  Record<
    BoggleRunSummaryTierLevel,
    Partial<{ bar: HTMLDivElement | null; ping: HTMLDivElement | null }>
  >
>

function playTierTickPing(pingEl: HTMLDivElement | null) {
  if (!pingEl) {
    return
  }

  pingEl.classList.remove(tierMeterStyles.pingActive)
  void pingEl.offsetWidth
  pingEl.classList.add(tierMeterStyles.pingActive)
  const onEnd = () => {
    pingEl.classList.remove(tierMeterStyles.pingActive)
    pingEl.removeEventListener('animationend', onEnd)
  }
  pingEl.addEventListener('animationend', onEnd, { once: true })
}

export function RunSummaryTierLine({
  isRevealed,
  line,
  longestWord,
  metricRampDurationMs = RUN_SUMMARY_METRIC_RAMP_MS,
  valueRef,
}: {
  isRevealed: boolean
  line: BoggleRunSummaryTierLine
  longestWord: null | string
  metricRampDurationMs?: number
  valueRef?: (el: HTMLDivElement | null) => void
}) {
  const tierMeterRef = useRef<RunSummaryTierMeterHandle | null>(null)
  const maxTierValue = line.category.tiers.at(-1)?.minInclusive ?? 1
  const [showTierLabel, setShowTierLabel] = useState(false)
  const qualifiedTierLevel = line.tier?.level

  const paintMeterFill = useCallback((metricValue: number) => {
    tierMeterRef.current?.paint(metricValue)
  }, [])

  const onMetricRampSettled = useCallback(
    (final: number) => {
      if (final !== line.metricValue || qualifiedTierLevel === undefined) {
        return
      }
      setShowTierLabel(true)
    },
    [line.metricValue, qualifiedTierLevel],
  )

  useEffect(() => {
    if (!isRevealed) {
      setShowTierLabel(false)
      tierMeterRef.current?.reset()
      tierMeterRef.current?.paint(0)
    }
  }, [isRevealed])

  useEffect(() => {
    setShowTierLabel(false)
  }, [line.metricValue, qualifiedTierLevel])

  return (
    <Group
      gap={20}
      justify="flex-start"
      align="center"
      wrap="nowrap"
      w="100%"
      pos="relative"
      key={line.category.id}
      opacity={isRevealed ? 1 : 0}
      style={{ transition: 'opacity 300ms ease-out' }}
    >
      <Stack gap={1} align="flex-end" w="50%" pos="relative" pt={10}>
        <Label c="rgba(255,255,255,0.8)" ta="right" fw={400}>
          {line.category.metricLabel}
        </Label>
        <RunSummaryTierMeter ref={tierMeterRef} line={line} />
        {line.category.id === 'pathfinder' && longestWord ? (
          <Text
            component="span"
            fz={13}
            lh={1}
            px={6}
            py={4}
            fw={400}
            lts={rem(1)}
            bottom={-26}
            right={0}
            style={{
              background: '#ffcf7317',
              border: '1px solid #ffffff1a',
              color: '#b7b18e',
              fontFamily: "'Noto Sans Variable'",
              position: 'absolute',
              pointerEvents: 'none',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {longestWord}
          </Text>
        ) : null}
      </Stack>
      <Stack
        ref={valueRef}
        pos="relative"
        style={{ display: 'inline-flex' }}
        pt={3}
        miw={36}
        gap={2}
      >
        <RampingNumberText
          fz={36}
          behavior="standard"
          duration={metricRampDurationMs}
          durationScaleReferenceValue={maxTierValue}
          formatValue={
            line.category.id === 'scholar'
              ? value => formatRunSummaryMetricValue(line.category.id, value)
              : undefined
          }
          maxValueVisual={Math.max(1, maxTierValue)}
          minScaledDuration={metricRampDurationMs <= 0 ? 0 : undefined}
          onDisplayValueChange={paintMeterFill}
          onRampSettled={onMetricRampSettled}
          value={isRevealed ? line.metricValue : 0}
        />
        {line.tier ? (
          <Text
            component="div"
            fz={12}
            lh={1.4}
            ta="left"
            c={RUN_SUMMARY_TIER_FILL_COLORS[line.tier.level]}
            ff="inherit"
            bottom={9}
            opacity={showTierLabel ? 1 : 0}
            style={{
              pointerEvents: 'none',
              position: 'relative',
              // left: 0,
              // top: '85%',
              transition: 'opacity 300ms ease-out',
            }}
          >
            {`Tier ${line.tier.level}`}
          </Text>
        ) : null}
      </Stack>
    </Group>
  )
}

const RunSummaryTierMeter = forwardRef<
  RunSummaryTierMeterHandle,
  { line: BoggleRunSummaryTierLine }
>(function RunSummaryTierMeter({ line }, ref) {
  const fillRef = useRef<HTMLDivElement>(null)
  const tickElsRef = useRef<TierTickRefs>({})
  const prevMetricRef = useRef<number | undefined>(undefined)

  const minMeterValue = line.category.meterMinInclusive ?? 0
  const maxTierValue = line.category.tiers.at(-1)?.minInclusive ?? 1
  const meterPixWidth = TIER_LINE_METER_WIDTH
  const usableTrackPx = meterPixWidth - RUN_SUMMARY_TIER_METER_END_GAP_PX
  const visualMeterMax =
    usableTrackPx > 0
      ? minMeterValue + ((maxTierValue - minMeterValue) * meterPixWidth) / usableTrackPx
      : maxTierValue
  const visualMeterRange = visualMeterMax - minMeterValue

  const attachTickBar = useCallback(
    (tierLevel: BoggleRunSummaryTierLevel, el: HTMLDivElement | null) => {
      if (!el) {
        return
      }
      if (!tickElsRef.current[tierLevel]) {
        tickElsRef.current[tierLevel] = {}
      }
      tickElsRef.current[tierLevel]!.bar = el
    },
    [],
  )

  const attachTickPing = useCallback(
    (tierLevel: BoggleRunSummaryTierLevel, el: HTMLDivElement | null) => {
      if (!el) {
        return
      }
      if (!tickElsRef.current[tierLevel]) {
        tickElsRef.current[tierLevel] = {}
      }
      tickElsRef.current[tierLevel]!.ping = el
    },
    [],
  )

  const paint = useCallback(
    (metricValue: number) => {
      const fillEl = fillRef.current

      for (const tier of line.category.tiers) {
        const bar = tickElsRef.current[tier.level]?.bar
        if (bar) {
          const reached = metricValue >= tier.minInclusive
          bar.style.backgroundColor = reached
            ? RUN_SUMMARY_TIER_METER_TICK_REACHED
            : RUN_SUMMARY_TIER_METER_TICK_UNREACHED
        }
      }

      const prev = prevMetricRef.current
      if (prev !== undefined) {
        for (const tier of line.category.tiers) {
          if (prev < tier.minInclusive && metricValue >= tier.minInclusive) {
            playTierTickPing(tickElsRef.current[tier.level]?.ping ?? null)
          }
        }
      }
      prevMetricRef.current = metricValue

      if (!fillEl) {
        return
      }

      const effectiveValue = Math.max(minMeterValue, metricValue)
      let fillPercent = 0
      if (visualMeterRange > 0) {
        const inTopTier = effectiveValue >= maxTierValue - 1e-9
        if (inTopTier) {
          fillPercent = 100
        } else {
          fillPercent = Math.min(
            100,
            Math.max(0, ((effectiveValue - minMeterValue) / visualMeterRange) * 100),
          )
        }
      }

      const tierAtDisplay = getTierForMetric(line.category.tiers, metricValue)
      fillEl.style.backgroundColor = tierAtDisplay
        ? RUN_SUMMARY_TIER_FILL_COLORS[tierAtDisplay.level]
        : RUN_SUMMARY_TIER_FILL_COLORS._none
      fillEl.style.boxShadow =
        tierAtDisplay?.level === 'V' ? RUN_SUMMARY_TIER_METER_MAX_GLOW : 'none'
      fillEl.style.opacity = fillPercent > 0 ? '0.95' : '0'
      fillEl.style.width = `${fillPercent}%`
    },
    [line.category.tiers, maxTierValue, minMeterValue, visualMeterRange],
  )

  const reset = useCallback(() => {
    prevMetricRef.current = undefined
  }, [])

  useImperativeHandle(ref, () => ({ paint, reset }), [paint, reset])

  return (
    <Box h={14} pos="relative" w={TIER_LINE_METER_WIDTH} bdrs={999}>
      <Box
        bg="rgba(255, 255, 255, 0.1)"
        bd="1px solid #ffffff14"
        bdrs={999}
        h={8}
        left={0}
        pos="absolute"
        right={0}
        top={4}
      />
      <Box
        ref={fillRef}
        bg={RUN_SUMMARY_TIER_FILL_COLORS._none}
        bdrs={999}
        h={8}
        left={0}
        opacity={0}
        pos="absolute"
        top={4}
        w={0}
      />
      {line.category.tiers.map(tier => {
        const tickPercent =
          visualMeterRange > 0
            ? Math.min(
                100,
                Math.max(
                  0,
                  ((tier.minInclusive - minMeterValue) / visualMeterRange) * 100,
                ),
              )
            : 0

        return (
          <Box
            h={10}
            key={tier.level}
            left={`${tickPercent}%`}
            pos="absolute"
            top={3}
            w={2}
            style={{ transform: 'translateX(-50%)' }}
          >
            <Box
              ref={el => attachTickBar(tier.level, el)}
              bg={RUN_SUMMARY_TIER_METER_TICK_UNREACHED}
              bdrs={1}
              h={10}
              left="50%"
              pos="absolute"
              top={0}
              // Width is intentionally px
              style={{ transform: 'translateX(-50%)', width: '2px' }}
            />
            <Box
              className={tierMeterStyles.ping}
              ref={el => attachTickPing(tier.level, el)}
            />
          </Box>
        )
      })}
    </Box>
  )
})
